import type { ComponentType } from 'react';
import { Box } from '@zillow/constellation-10';
import { ErrorPage } from '@zg-rentals/visual-cohesion';
import { rentalManagerLoginOptionsForUser } from '@zg-rentals/login-zillow';
import {
  buildMfaStepUpRetryHandler,
  useClearMfaRetryParam,
  zillowLoginRedirectHref,
} from '@zg-rentals/login-zillow/client';
import type { useAuth } from '../AuthProvider';
import { Auth, useAuth as useAuthHook } from '../AuthProvider';
import pathsManifest from '../utils/pathsManifest';
import StringConstants from '../constants/StringConstants';

type AuthUser = Exclude<ReturnType<typeof useAuth>['user'], null | undefined>;

const APP_NAME = 'satellite-landlord-web';

const paths = pathsManifest();
const isDevelopment = process.env.NODE_ENV === 'development';
const ctaText = isDevelopment ? 'Return to SLW Dev Tools Page' : StringConstants.GO_TO_RENTAL_MANAGER;
const ctaUrl = isDevelopment ? paths.devToolsPage : paths.homePage;

const onError = (errorMessage: string, errorCode: number) => (
  // Renders in place of the page Component (no Layout/header/footer), so it fills the full
  // viewport rather than using ErrorPageContainer's chrome-aware heights.
  <Box css={{ height: '100dvh' }}>
    <ErrorPage
      errorMessage={errorMessage}
      errorDetails={`Error code: ${errorCode}`}
      action={<a href={ctaUrl}>{ctaText}</a>}
    />
  </Box>
);

export function getIsProd() {
  return typeof window !== 'undefined' && window.location.origin.includes('zillow.com');
}

export function getRedirectHref(user?: AuthUser | null) {
  return zillowLoginRedirectHref({
    baseURL: process.env.NEXT_PUBLIC_AUTH_BASE_URL,
    entryPoint: APP_NAME,
    forwardPrefix: process.env.NEXT_PUBLIC_AUTH_FORWARD_PREFIX,
    ...rentalManagerLoginOptionsForUser(user),
  });
}

const defaultBoundaryErrorProps = {
  onError: onError('unknown error', 500),
  onUnauthorized: () => onError('unauthorized', 401),
};

export function withRedirectBoundary<Props extends { user: AuthUser }>(
  Component: ComponentType<Props>,
  boundaryProps?: { loading?: React.ReactNode },
) {
  const Wrapped = (props: Omit<Props, 'user'>) => {
    const { user } = useAuthHook();
    useClearMfaRetryParam({ user });
    const redirectHref = getRedirectHref(user);

    const handleUnauthorized = buildMfaStepUpRetryHandler({
      appName: APP_NAME,
      buildNumber: process?.env?.NEXT_PUBLIC_BUILD_NUMBER,
      user,
      redirectHref,
      fallback: defaultBoundaryErrorProps.onUnauthorized,
      redirecting: boundaryProps?.loading ?? <>Redirecting...</>,
    });

    return (
      <Auth.Boundary
        {...defaultBoundaryErrorProps}
        {...boundaryProps}
        redirectHref={redirectHref}
        onUnauthorized={handleUnauthorized}
      >
        {(authorizedUser) => <Component {...(props as Props)} user={authorizedUser} />}
      </Auth.Boundary>
    );
  };
  Wrapped.displayName = `withRedirectBoundary(${Component.displayName ?? Component.name ?? 'Component'})`;
  return Wrapped;
}
